import type { AnimationConfig, AnimationItem, RendererType } from 'lottie-web';
import lottie from 'lottie-web';
import { useEffect, useRef } from 'react';

export interface BaseLottieConfig<T extends RendererType> {
  readonly animationData: Promise<unknown> | unknown;
  readonly lottieConfig?: Partial<AnimationConfig<T>>;
}

const useLottie = <T extends RendererType = 'svg'>(
  elementRef: React.MutableRefObject<Element | null>,
  { animationData, lottieConfig = {} }: BaseLottieConfig<T>,
): AnimationItem | undefined => {
  const lottieRef: React.MutableRefObject<AnimationItem | undefined> = useRef();

  useEffect(() => {
    if (elementRef.current == null) return;
    if (lottieRef.current != null) return;

    lottieRef.current = lottie.loadAnimation({
      container: elementRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      ...lottieConfig,
      rendererSettings: {
        ...lottieConfig.rendererSettings,
        focusable: false,
        title: '',
        description: '',
        ariaHidden: true,
      },
      animationData,
    });
  }, [elementRef, animationData, lottieConfig]);

  return lottieRef.current;
};

export default useLottie;
